import { BACKEND_ORIGIN } from '@/lib/apiBase'

// ─── Types extraction IA ───────────────────────────────────────────────────────

export type ExtractionResult = {
  id: string
  fieldId: string
  extractedValue: string
  confidence: number
  documentUrl: string
  acceptedByUser: boolean
}

export type ExtractResponse = {
  extractions: ExtractionResult[]
  documentQualityScore: number
}

/** Erreur avec code HTTP — permet de discriminer 401 vs 500 sans string-matching fragile */
export class PortalApiError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message)
    this.name = 'PortalApiError'
  }
}

export type AnswerMap = Record<string, string | number | null>

export type TemplateField = {
  id: string
  label: string
  type: 'text' | 'number' | 'select' | 'file' | 'date'
  unit?: string
  description?: string
  required: boolean
  options?: string[]
}

export type TemplateSection = {
  id: string
  title: string
  order?: number
  fields: TemplateField[]
}

export type ComplementRequestSummary = {
  id: string
  requestedFieldIds: string[]
  message: string | null
  requestedAt: string
  requestedBy: string
}

export type PortalData = {
  survey: {
    id: string
    title: string
    status: string
    openedAt: string | null
  }
  campaign: {
    id: string
    name: string
  }
  organization: {
    name: string
  }
  template: {
    id: string
    name: string
    sections: TemplateSection[]
  }
  complementRequest: ComplementRequestSummary | null
}

// Analogue à apiFetch mais SANS Bearer header — le token est dans l'URL
export async function portalFetch<T = unknown>(
  token: string,
  path: string,
  options?: RequestInit
): Promise<T> {
  const url = `${BACKEND_ORIGIN}/portal/${token}${path}`
  const isBodyRequest =
    options?.method === 'POST' || options?.method === 'PUT' || options?.method === 'PATCH'
  const headers: Record<string, string> = {
    ...(isBodyRequest ? { 'Content-Type': 'application/json' } : {}),
    ...(options?.headers as Record<string, string> | undefined),
  }
  const res = await fetch(url, { ...options, headers })
  if (!res.ok) {
    const text = await res.text()
    let message = `Portal error ${res.status}: ${text}`
    try {
      const json = JSON.parse(text) as { error?: string; detail?: unknown }
      if (typeof json.error === 'string') message = json.error
    } catch { /* not JSON — keep raw message */ }
    throw new PortalApiError(res.status, message)
  }
  return res.json() as Promise<T>
}

export async function fetchPortalData(token: string): Promise<PortalData> {
  return portalFetch<PortalData>(token, '')
}

export async function recordConsent(
  token: string
): Promise<{ consentRecorded: boolean; consentAt: string }> {
  return portalFetch<{ consentRecorded: boolean; consentAt: string }>(token, '/consent', {
    method: 'POST',
    body: JSON.stringify({ consentGiven: true }),
  })
}

export async function loadProgress(token: string): Promise<{ answers: AnswerMap }> {
  return portalFetch<{ answers: AnswerMap }>(token, '/progress')
}

export async function saveProgress(
  token: string,
  answers: AnswerMap
): Promise<{ saved: boolean; savedAt: string }> {
  return portalFetch<{ saved: boolean; savedAt: string }>(token, '/progress', {
    method: 'PUT',
    body: JSON.stringify({ answers }),
  })
}

export async function submitPortal(
  token: string
): Promise<{ submitted: boolean; submittedAt: string }> {
  return portalFetch<{ submitted: boolean; submittedAt: string }>(token, '/submit', {
    method: 'POST',
    body: JSON.stringify({}),
  })
}

export async function acceptExtractionValue(
  token: string,
  extractionId: string
): Promise<{ updated: boolean }> {
  return portalFetch<{ updated: boolean }>(token, `/extractions/${extractionId}`, {
    method: 'PATCH',
    body: JSON.stringify({ acceptedByUser: true }),
  })
}

// Upload multipart — NE PAS utiliser portalFetch (force Content-Type: application/json → boundary manquant)
export async function uploadDocumentForExtraction(
  token: string,
  file: File
): Promise<ExtractResponse> {
  const formData = new FormData()
  formData.append('file', file) // le champ s'appelle 'file' (multer.single('file'))

  // NE PAS passer Content-Type — le navigateur auto-set 'multipart/form-data; boundary=...'
  const res = await fetch(`${BACKEND_ORIGIN}/portal/${token}/extract`, {
    method: 'POST',
    body: formData,
  })

  if (!res.ok) {
    const text = await res.text()
    let message = `Portal error ${res.status}: ${text}`
    try {
      const json = JSON.parse(text) as { error?: string }
      if (typeof json.error === 'string') message = json.error
    } catch { /* not JSON — keep raw message */ }
    throw new PortalApiError(res.status, message)
  }
  return res.json() as Promise<ExtractResponse>
}
